<template>
    <el-card class="salesforce-contact-settings-card mb-3 mt-3">
        <!-- Start loader -->
        <div class="text-center mt-2"
             v-if="isLoading">
            <h5>
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading...</span>
            </h5>
        </div>
        <!-- End loader -->
        <div v-else>
            <div class="mb-3">
                <el-row>
                    <span>Control whether contact changes are sent to Salesforce, received from Salesforce, or both by selecting the appropriate direction.</span>
                </el-row>
                <el-row class="mt-1">
                    <span>
                      <b>Best Practices</b>: Both Salesforce and {{ statics }} <b>can update</b> contacts and/or leads information (<i
                        class="fas fa-arrows-alt-h"></i>), but a good practice used, {{ statics }} <b>cannot create</b> new contacts/leads (<i class="fas fa-arrow-left"></i>) in Salesforce.
                    </span>
                </el-row>
            </div>

            <!-- Start Contact Configuration -->
            <div class="d-flex">
                <h5>Contact</h5>
                <el-tooltip
                        class="item"
                        effect="dark"
                        placement="top">
                    <div slot="content">
                        <p v-if="!hasFeatureVersion2 && !model.settings.contact_settings.enable_lead">Enable Contact as default Salesforce
                            CRM Module</p>
                        <p v-else-if="!hasFeatureVersion2">Contacts can't be enabled if Lead is already enabled</p>
                        <p v-else-if="hasFeatureVersion2 && hasAccountsFeature && !model.settings.contact_settings.enable_lead && !model.settings.contact_settings.enable_account">At least one module (Contact, Lead, or Account) must remain enabled.</p>
                        <p v-else-if="hasFeatureVersion2 && !hasAccountsFeature && !model.settings.contact_settings.enable_lead">At least one module (Contact or Lead) must remain enabled.</p>
                        <p v-else-if="model.settings.contact_settings.enable_contact">Disable Contact</p>
                        <p v-else>Enable Contact</p>
                    </div>
                    <el-switch
                            v-model="model.settings.contact_settings.enable_contact"
                            :disabled="isContactToggleDisabled"
                            active-color="#00a344"
                            class="mt-1 ml-2"
                    />
                </el-tooltip>
            </div>

            <div class="row form-group mt-3">
                <!-- Start Aloware and Salesforce Labels -->
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Aloware</small>
                </div>
                <div class="col-md-2 mb-1 text-center">
                    <small class="font-weight-bold">Direction</small>
                </div>
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Salesforce</small>
                </div>
            </div>
            <!-- End Aloware and Salesforce Labels -->

            <!-- Start Aloware Fields -->
            <div class="row form-group mt-3">
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_create"
                            :value="getSalesforceLabels('contact.create')"
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.contacts.create_direction"
                                        :disabled="!model.settings.contact_settings.enable_contact
                                || !model.settings.contact_settings.contacts.should_create"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.contacts.create_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_create"
                            :value="getSalesforceLabels('contact.create')"
                    />
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Delete button -->
                <div class="col-md-2 text-center"
                    v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="top">
                        <div slot="content">
                            <p>Enable creating a new contact in Salesforce when a new contact is created in
                                Aloware</p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.contacts.should_create"
                                :disabled="!model.settings.contact_settings.enable_contact"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Delete Button -->
            </div>
            <!-- End Contact Configuration -->

            <!-- Start Aloware Fields -->
            <div class="row form-group mt-3">
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_update"
                            :value="getSalesforceLabels('contact.update')"
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.contacts.update_direction"
                                        :disabled="!isDirectionEditable(getSalesforceLabels('contact.update'))
                                  || !model.settings.contact_settings.enable_contact
                                  || !model.settings.contact_settings.contacts.should_update"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.contacts.update_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            readonly
                            :disabled="!model.settings.contact_settings.enable_contact || !model.settings.contact_settings.contacts.should_update"
                            :value="getSalesforceLabels('contact.update')"
                    />
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Enable button -->
                <div class="col-md-2 text-center"
                    v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="top">
                        <div slot="content">
                            <p>Enable updating a contact in Salesforce when a contact is updated in Aloware</p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.contacts.should_update"
                                :disabled="!model.settings.contact_settings.enable_contact"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Enable Button -->
            </div>
            <!-- End Contact Configuration -->

            <!-- Start Lead Configuration -->
            <div class="d-flex">
                <h5>Lead</h5>
                <el-tooltip
                        class="item"
                        effect="dark"
                        placement="right">
                    <div slot="content">
                        <p v-if="!hasFeatureVersion2 && !model.settings.contact_settings.enable_contact">Enable Lead as
                            default Salesforce
                            CRM Module</p>
                        <p v-else-if="!hasFeatureVersion2">Leads can't be enabled if Contact is already enabled</p>
                        <p v-else-if="hasFeatureVersion2 && hasAccountsFeature && !model.settings.contact_settings.enable_contact && !model.settings.contact_settings.enable_account">At least one module (Contact, Lead, or Account) must remain enabled.</p>
                        <p v-else-if="hasFeatureVersion2 && !hasAccountsFeature && !model.settings.contact_settings.enable_contact">At least one module (Contact or Lead) must remain enabled.</p>
                        <p v-else-if="model.settings.contact_settings.enable_lead">Disable Lead</p>
                        <p v-else>Enable Lead</p>
                    </div>
                    <el-switch
                            v-model="model.settings.contact_settings.enable_lead"
                            :disabled="isLeadToggleDisabled"
                            class="mt-1 ml-2"
                            active-color="#00a344"
                    />
                </el-tooltip>
            </div>

            <div class="row form-group mt-3">
                <!-- Start Aloware and Salesforce Labels -->
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Aloware</small>
                </div>
                <div class="col-md-2 mb-1 text-center">
                    <small class="font-weight-bold">Direction</small>
                </div>
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Salesforce</small>
                </div>
            </div>
            <!-- End Aloware and Salesforce Labels -->

            <div class="row form-group mt-3">
                <!-- Start Aloware Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('contact.create')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_create"
                            readonly
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.leads.create_direction"
                                        :disabled="!model.settings.contact_settings.enable_lead
                                || !model.settings.contact_settings.leads.should_create"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.leads.create_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('lead.create')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_create"
                            readonly
                    >
                    </el-input>
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Delete button -->
                <div class="col-md-2 text-center"
                     v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="right">
                        <div slot="content">
                            <p> Enable creating a new lead in Salesforce when a new contact is created in
                                Aloware </p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.leads.should_create"
                                :disabled="!model.settings.contact_settings.enable_lead"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Delete Button -->
            </div>

            <div class="row form-group mt-3">
                <!-- Start Aloware Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('contact.update')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_update"
                            readonly
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.leads.update_direction"
                                        :disabled="!isDirectionEditable(getSalesforceLabels('lead.update'))
                                  || !model.settings.contact_settings.enable_lead
                                  || !model.settings.contact_settings.leads.should_update"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.leads.update_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('lead.update')"
                            :disabled="!model.settings.contact_settings.enable_lead || !model.settings.contact_settings.leads.should_update"
                            readonly
                    >
                    </el-input>
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Delete button -->
                <div class="col-md-2 text-center"
                    v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="right">
                        <div slot="content">
                            <p> Enable updating a lead in Salesforce when a contact is updated in Aloware </p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.leads.should_update"
                                :disabled="!model.settings.contact_settings.enable_lead"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Delete Button -->
            </div>
            <!-- End Lead Configuration -->
            
            <!-- Start Account Configuration -->
            <div class="d-flex mt-3" v-if="hasAccountsFeature">
                <h5>Account</h5>
                <el-tooltip
                        class="item"
                        effect="dark"
                        placement="right">
                    <div slot="content">
                        <p v-if="!hasFeatureVersion2 && (!model.settings.contact_settings.enable_contact && !model.settings.contact_settings.enable_lead)">Enable Account as Salesforce Module</p>
                        <p v-else-if="hasFeatureVersion2 && model.settings.contact_settings.enable_account && !model.settings.contact_settings.enable_contact && !model.settings.contact_settings.enable_lead">At least one module (Contact, Lead, or Account) must remain enabled.</p>
                        <p v-else-if="hasFeatureVersion2 && !model.settings.contact_settings.enable_account">Enable Account</p>
                        <p v-else-if="model.settings.contact_settings.enable_account">Disable Account</p>
                    </div>
                    <el-switch
                            v-model="model.settings.contact_settings.enable_account"
                            :disabled="isAccountToggleDisabled"
                            class="mt-1 ml-2"
                            active-color="#00a344"
                    />
                </el-tooltip>
            </div>

            <div class="row form-group mt-3" v-if="hasAccountsFeature">
                <!-- Start Aloware and Salesforce Labels -->
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Aloware</small>
                </div>
                <div class="col-md-2 mb-1 text-center">
                    <small class="font-weight-bold">Direction</small>
                </div>
                <div class="mb-1" :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <small class="font-weight-bold">Salesforce</small>
                </div>
            </div>

            <div class="row form-group mt-3" v-if="hasAccountsFeature">
                <!-- Start Aloware Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('account.create')"
                            :disabled="!model.settings.contact_settings.enable_account || !model.settings.contact_settings.accounts.should_create"
                            readonly
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.accounts.create_direction"
                                        :disabled="!model.settings.contact_settings.enable_account
                                || !model.settings.contact_settings.accounts.should_create"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.accounts.create_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('account.create')"
                            :disabled="!model.settings.contact_settings.enable_account || !model.settings.contact_settings.accounts.should_create"
                            readonly
                    >
                    </el-input>
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Enable button -->
                <div class="col-md-2 text-center"
                     v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="right">
                        <div slot="content">
                            <p> Enable creating a new account in Salesforce when an account is created in
                                {{ statics }} </p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.accounts.should_create"
                                :disabled="!model.settings.contact_settings.enable_account"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Enable Button -->
            </div>

            <div class="row form-group mt-3" v-if="hasAccountsFeature">
                <!-- Start Aloware Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('account.update')"
                            :disabled="!model.settings.contact_settings.enable_account || !model.settings.contact_settings.accounts.should_update"
                            readonly
                    />
                </div>
                <!-- End Aloware Fields -->

                <div class="col-md-2 text-center">
                    <direction-selector :initial-state="model.settings.contact_settings.accounts.update_direction"
                                        :disabled="!isDirectionEditable(getSalesforceLabels('account.update'))
                                  || !model.settings.contact_settings.enable_account
                                  || !model.settings.contact_settings.accounts.should_update"
                                        :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_BI, IntegrationPropertyMapDirections.DIRECTION_INBOUND]"
                                        @change="direction => model.settings.contact_settings.accounts.update_direction = direction">
                    </direction-selector>
                </div>

                <!-- Start Salesforce Fields -->
                <div :class="hasFeatureVersion2 ? 'col-md-5' : 'col-md-4'">
                    <el-input
                            :value="getSalesforceLabels('account.update')"
                            :disabled="!model.settings.contact_settings.enable_account || !model.settings.contact_settings.accounts.should_update"
                            readonly
                    >
                    </el-input>
                </div>
                <!-- End Salesforce Fields -->

                <!-- Start Enable button -->
                <div class="col-md-2 text-center"
                    v-if="!hasFeatureVersion2">
                    <el-tooltip
                            class="item"
                            effect="dark"
                            placement="right">
                        <div slot="content">
                            <p> Enable updating an account in Salesforce when an account is updated in {{ statics }} </p>
                        </div>
                        <el-switch
                                v-model="model.settings.contact_settings.accounts.should_update"
                                :disabled="!model.settings.contact_settings.enable_account"
                                class="mt-1 ml-2"
                                active-color="#00a344"
                        />
                    </el-tooltip>
                </div>
                <!-- End Enable Button -->
            </div>
            <!-- End Account Configuration -->
            <!-- Start Prioritization Section -->
            <template
                    v-if="hasFeatureVersion2 && (model.settings.contact_settings.enable_contact + model.settings.contact_settings.enable_lead + model.settings.contact_settings.enable_account) >= 2">
                <el-alert class="mt-2"
                          type="warning"
                          :closable="false">
                    <div class="sync-priority-content text-black">
                        <p class="sync-priority-description">
                            <b class="section-subheader">Sync Object Priority Order</b><br/>
                            When multiple Salesforce objects share the same phone number, choose the order of priority.
                            The first available record in this order will be used as primary in {{ statics }}.
                        </p>
                        <div class="sync-priority-reorder">
                            <div class="sync-priority-hint mb-1">Drag to reorder priority</div>
                            <draggable
                                    tag="ul"
                                    class="sync-priority-list list-unstyled"
                                    handle=".handle"
                                    :list="priority_items"
                                    @change="onPriorityOrderChanged">
                                <li v-for="mod in priority_items"
                                    :key="mod"
                                    class="sync-priority-item d-flex align-items-center">
                                    <el-button size="mini" class="handle mr-2 draggable">
                                        <i class="fas fa-arrows-alt-v"></i>
                                    </el-button>
                                    <span class="flex-grow-1">{{ getModuleDisplayName(mod) }}</span>
                                    <el-tag size="mini"
                                            :type="isModuleEnabled(mod) ? 'success' : 'info'">
                                        {{ isModuleEnabled(mod) ? 'Enabled' : 'Disabled' }}
                                    </el-tag>
                                </li>
                            </draggable>
                        </div>
                    </div>
                </el-alert>
            </template>
            <!-- End Prioritization Section -->
        </div>
    </el-card>
</template>

<script>
import DirectionSelector from "../direction-selector.vue"
import * as IntegrationPropertyMapDirections from '../../../constants/integration-property-map-directions'
import * as SalesforceContactSettingsLabels from "../../../constants/integration-salesforce-contact-settings-labels"
import {mapActions} from 'vuex'
import {MODULE_LEAD, MODULE_CONTACT, MODULE_ACCOUNT} from "../../../constants/integration_modules";
import draggable from 'vuedraggable'

export default {
    name: 'salesforce-contact-settings',
    components: {DirectionSelector, draggable},
    props: {
        model: {
            type: Object | null,
            required: true
        },
        statics: {
            type: String,
            required: true,
        }
    },
    data: () => ({
        // ui
        isLoading: false,
        IntegrationPropertyMapDirections,
        default_settings: {},
        MODULE_LEAD,
        MODULE_CONTACT,
        MODULE_ACCOUNT,
        priority_items: [],
    }),
    computed: {
        hasFeatureVersion2() {
            return this.model?.credentials?.feature_version >= 2
        },
        hasAccountsFeature() {
            return this.model?.credentials?.feature_version >= 5
        },
        isContactToggleDisabled() {
            if (!this.hasFeatureVersion2) {
                return this.model.settings.contact_settings.enable_lead
            }

            if (this.hasAccountsFeature) {
                return this.model.settings.contact_settings.enable_contact
                    && !this.model.settings.contact_settings.enable_lead
                    && !this.model.settings.contact_settings.enable_account
            }

            return this.model.settings.contact_settings.enable_contact
                && !this.model.settings.contact_settings.enable_lead
        },
        isLeadToggleDisabled() {
            if (!this.hasFeatureVersion2) {
                return this.model.settings.contact_settings.enable_contact
            }

            if (this.hasAccountsFeature) {
                return this.model.settings.contact_settings.enable_lead
                    && !this.model.settings.contact_settings.enable_contact
                    && !this.model.settings.contact_settings.enable_account
            }

            return this.model.settings.contact_settings.enable_lead
                && !this.model.settings.contact_settings.enable_contact
        },
        isAccountToggleDisabled() {
            if (!this.hasFeatureVersion2) {
                return false
            }

            return this.model.settings.contact_settings.enable_account
                && !this.model.settings.contact_settings.enable_contact
                && !this.model.settings.contact_settings.enable_lead
        }
    },
    async created() {
        this.isLoading = true
        this.resetSalesforceSettings()
        this.initializePriorityItems()
        this.isLoading = false
    },
    methods: {
        ...mapActions(['setRefreshPageIsNeeded', 'setCRMModuleWasChanged']),
        resetSalesforceSettings() {
            if (!this.model.settings) {
                this.$set(this.model, 'settings', {});
            }
            if (!this.model.settings.contact_settings) {
                this.$set(this.model.settings, 'contact_settings', {
                    enable_contact: false,
                    enable_lead: true,
                    enable_account: false,
                    contacts: {
                        should_create: true,
                        create_direction: IntegrationPropertyMapDirections.DIRECTION_INBOUND,
                        should_update: true,
                        update_direction: IntegrationPropertyMapDirections.DIRECTION_BI,
                    },
                    leads: {
                        should_create: true,
                        create_direction: IntegrationPropertyMapDirections.DIRECTION_INBOUND,
                        should_update: true,
                        update_direction: IntegrationPropertyMapDirections.DIRECTION_BI,
                    },
                    accounts: {
                        should_create: true,
                        create_direction: IntegrationPropertyMapDirections.DIRECTION_INBOUND,
                        should_update: true,
                        update_direction: IntegrationPropertyMapDirections.DIRECTION_BI,
                    }
                });
            }

            // back-compat: ensure accounts structure exists with defaults only when feature is supported
            if (this.hasAccountsFeature) {
                if (!this.model.settings.contact_settings.hasOwnProperty('enable_account')) {
                    this.$set(this.model.settings.contact_settings, 'enable_account', false)
                }
                if (!this.model.settings.contact_settings.hasOwnProperty('accounts') || typeof this.model.settings.contact_settings.accounts !== 'object') {
                    this.$set(this.model.settings.contact_settings, 'accounts', {
                        should_create: true,
                        create_direction: IntegrationPropertyMapDirections.DIRECTION_INBOUND,
                        should_update: true,
                        update_direction: IntegrationPropertyMapDirections.DIRECTION_INBOUND,
                    })
                } else {
                    const acc = this.model.settings.contact_settings.accounts
                    if (!acc.hasOwnProperty('should_create')) this.$set(acc, 'should_create', true)
                    if (!acc.hasOwnProperty('create_direction')) this.$set(acc, 'create_direction', IntegrationPropertyMapDirections.DIRECTION_INBOUND)
                    if (!acc.hasOwnProperty('should_update')) this.$set(acc, 'should_update', true)
                    if (!acc.hasOwnProperty('update_direction')) this.$set(acc, 'update_direction', IntegrationPropertyMapDirections.DIRECTION_INBOUND)
                }
            }

            // this is new flag and can be not able in old payloads
            if (this.hasFeatureVersion2) {
                // migrate legacy single priority to ordered priorities array
                if (!this.model.settings.contact_settings.hasOwnProperty('priorities')) {
                    const contact_settings = this.model.settings.contact_settings
                    const priorities = []
                    if (contact_settings.enable_lead) priorities.push(MODULE_LEAD)
                    if (contact_settings.enable_contact) priorities.push(MODULE_CONTACT)
                    if (this.hasAccountsFeature && contact_settings.enable_account) priorities.push(MODULE_ACCOUNT)

                    const legacy_priority = contact_settings.priority
                    if (legacy_priority && priorities.includes(legacy_priority)) {
                        const idx = priorities.indexOf(legacy_priority)
                        priorities.splice(idx, 1)
                        priorities.unshift(legacy_priority)
                    }

                    this.$set(this.model.settings.contact_settings, 'priorities', priorities)
                } else if (!this.hasAccountsFeature) {
                    // strip accounts from priorities when feature is disabled
                    const filtered = (this.model.settings.contact_settings.priorities || []).filter(m => m !== MODULE_ACCOUNT)
                    this.$set(this.model.settings.contact_settings, 'priorities', filtered)
                }
            }

            this.default_settings = _.clone(this.model?.settings?.contact_settings)
        },
        initializePriorityItems() {
            const allowed = [this.MODULE_LEAD, this.MODULE_CONTACT].concat(this.hasAccountsFeature ? [this.MODULE_ACCOUNT] : [])
            const current = Array.isArray(this.model.settings?.contact_settings?.priorities)
                ? this.model.settings.contact_settings.priorities
                : []
            const uniqueCurrent = current.filter(m => allowed.includes(m))
            const missing = allowed.filter(m => !uniqueCurrent.includes(m))
            this.priority_items = [...uniqueCurrent, ...missing]
            this.model.settings.contact_settings.priorities = [...this.priority_items]
        },
        onPriorityOrderChanged() {
            // Persist order back to model settings without coupling to enable flags
            const allowed = [this.MODULE_LEAD, this.MODULE_CONTACT].concat(this.hasAccountsFeature ? [this.MODULE_ACCOUNT] : [])
            const sanitized = this.priority_items.filter(m => allowed.includes(m))
            this.$set(this.model.settings.contact_settings, 'priorities', sanitized)
        },
        getModuleDisplayName(module_key) {
            switch (module_key) {
                case this.MODULE_LEAD:
                    return 'Lead'
                case this.MODULE_CONTACT:
                    return 'Contact'
                case this.MODULE_ACCOUNT:
                    return 'Account'
                default:
                    return module_key
            }
        },
        isModuleEnabled(module_key) {
            switch (module_key) {
                case this.MODULE_LEAD:
                    return !!this.model.settings.contact_settings.enable_lead
                case this.MODULE_CONTACT:
                    return !!this.model.settings.contact_settings.enable_contact
                case this.MODULE_ACCOUNT:
                    return !!this.model.settings.contact_settings.enable_account
                default:
                    return false
            }
        },
        isDirectionEditable(key) {
            switch (key) {
                case SalesforceContactSettingsLabels.LABEL_CONTACT_CREATE:
                case SalesforceContactSettingsLabels.LABEL_LEAD_CREATE:
                case SalesforceContactSettingsLabels.LABEL_ACCOUNT_CREATE:
                    return false
                case SalesforceContactSettingsLabels.LABEL_CONTACT_UPDATE:
                case SalesforceContactSettingsLabels.LABEL_LEAD_UPDATE:
                case SalesforceContactSettingsLabels.LABEL_ACCOUNT_UPDATE:
                    return true
                default:
                    return true
            }
        },
        getSalesforceLabels(module_operation) {
            switch (module_operation) {
                case 'contact.create':
                    return SalesforceContactSettingsLabels.LABEL_CONTACT_CREATE
                case 'contact.update':
                    return SalesforceContactSettingsLabels.LABEL_CONTACT_UPDATE
                case 'lead.create':
                    return SalesforceContactSettingsLabels.LABEL_LEAD_CREATE
                case 'lead.update':
                    return SalesforceContactSettingsLabels.LABEL_LEAD_UPDATE
                case 'account.create':
                    return SalesforceContactSettingsLabels.LABEL_ACCOUNT_CREATE
                case 'account.update':
                    return SalesforceContactSettingsLabels.LABEL_ACCOUNT_UPDATE
                default:
                    return ''
            }
        }
    },
    watch: {
        'model.settings.contact_settings.enable_contact': function (data) {
            const flag = data !== this.default_settings.enable_contact || this.model.settings.contact_settings.enable_lead !== this.default_settings.enable_lead || this.model.settings.contact_settings.enable_account !== this.default_settings.enable_account
            this.setCRMModuleWasChanged(flag)
            if (this.model.credentials?.feature_version >= 2) {
                this.setRefreshPageIsNeeded((!this.default_settings.enable_contact && data)
                    || (!this.default_settings.enable_lead && this.model.settings.contact_settings.enable_lead)
                    || (!this.default_settings.enable_account && this.model.settings.contact_settings.enable_account))
            } else {
                this.setRefreshPageIsNeeded(flag)
            }
        },
        'model.settings.contact_settings.enable_lead': function (data) {
            const flag = this.model.settings.contact_settings.enable_contact !== this.default_settings.enable_contact || data !== this.default_settings.enable_lead || this.model.settings.contact_settings.enable_account !== this.default_settings.enable_account
            this.setCRMModuleWasChanged(flag)
            if (this.model.credentials?.feature_version >= 2) {
                this.setRefreshPageIsNeeded((!this.default_settings.enable_lead && data)
                    || (!this.default_settings.enable_contact && this.model.settings.contact_settings.enable_contact)
                    || (!this.default_settings.enable_account && this.model.settings.contact_settings.enable_account))
            } else {
                this.setRefreshPageIsNeeded(flag)
            }
        },
        'model.settings.contact_settings.enable_account': function (data) {
            const flag = this.model.settings.contact_settings.enable_contact !== this.default_settings.enable_contact || this.model.settings.contact_settings.enable_lead !== this.default_settings.enable_lead || data !== this.default_settings.enable_account
            this.setCRMModuleWasChanged(flag)
            if (this.model.credentials?.feature_version >= 2) {
                this.setRefreshPageIsNeeded((!this.default_settings.enable_account && data)
                    || (!this.default_settings.enable_contact && this.model.settings.contact_settings.enable_contact)
                    || (!this.default_settings.enable_lead && this.model.settings.contact_settings.enable_lead))
            } else {
                this.setRefreshPageIsNeeded(flag)
            }
        }
    }
}
</script>

<style scoped>

.sync-priority-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}

.sync-priority-description {
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    flex: 1;
}

.sync-priority-select {
    width: 200px;
    flex-shrink: 0;
}

/* Override element-ui select styles */
.sync-priority-select :deep(.el-input__inner) {
    height: 36px;
    line-height: 36px;
    border-color: #d1d5db;
}

.sync-priority-reorder {
    width: 320px;
    flex-shrink: 0;
}

.sync-priority-list {
    margin: 0;
    padding: 0;
}

.sync-priority-item {
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 6px 8px;
    margin-bottom: 8px;
}

/* Draggable handle affordance */
.sync-priority-item .handle {
    cursor: grab;
}

.sync-priority-item .handle:active {
    cursor: grabbing;
}
</style>
